import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface ExamRequest {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  examType: string;
  status: string;
  examCode?: string;
  notes?: string;
  examDate?: string;
  examTime?: string;
  createdAt: string;
  user: { name: string; email: string };
  course: { title: string };
}

export default function AdminExamRequestsPage() {
  const [selectedRequest, setSelectedRequest] = useState<ExamRequest | null>(null);
  const [examCode, setExamCode] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("approved");
  const queryClient = useQueryClient();

  const { data: requests, isLoading } = useQuery<ExamRequest[]>({
    queryKey: ['admin-exam-requests'],
    queryFn: async () => {
      const response = await fetch('/api/admin/exam-requests', {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch requests');
      const data = await response.json();
      return data.requests;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status, examCode, notes }: any) => {
      const response = await fetch(`/api/admin/exam-requests/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status, examCode, notes }),
      });
      if (!response.ok) throw new Error('Failed to update request');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-exam-requests'] });
      setSelectedRequest(null);
      setExamCode("");
      setNotes("");
    },
  });

  const handleUpdateStatus = async () => {
    if (!selectedRequest) return;
    
    if (status === "approved" && !examCode) {
      alert("Exam code is required for approval");
      return;
    }

    updateMutation.mutate({
      id: selectedRequest.id,
      status,
      examCode: examCode || undefined,
      notes: notes || undefined,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "approved": return "bg-green-100 text-green-800 border-green-200";
      case "rejected": return "bg-red-100 text-red-800 border-red-200";
      case "completed": return "bg-blue-100 text-blue-800 border-blue-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending": return <Clock className="w-4 h-4" />;
      case "approved": return <CheckCircle2 className="w-4 h-4" />;
      case "rejected": return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading exam requests...</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold">Exam Requests</h1>
        <p className="text-gray-500 mt-2">Manage student exam requests and issue exam codes</p>
      </div>

      {!requests || requests.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-gray-500">No exam requests yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {requests.map((request) => (
            <Card key={request.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">
                        {request.firstName} {request.lastName}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border flex items-center gap-1 ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                      <div>
                        <p className="font-semibold text-gray-800">Email</p>
                        <p>{request.email}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">Course</p>
                        <p>{request.course.title}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">Exam Type</p>
                        <p className="capitalize">{request.examType.replace("-", " ")}</p>
                      </div>
                      {request.examDate && (
                        <>
                          <div>
                            <p className="font-semibold text-gray-800">Exam Date</p>
                            <p>{new Date(request.examDate).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800">Exam Time</p>
                            <p>{request.examTime}</p>
                          </div>
                        </>
                      )}
                      <div>
                        <p className="font-semibold text-gray-800">Requested</p>
                        <p>{new Date(request.createdAt).toLocaleDateString()}</p>
                      </div>
                      {request.examCode && (
                        <div>
                          <p className="font-semibold text-gray-800">Exam Code</p>
                          <p className="font-mono bg-gray-100 px-2 py-1 rounded">{request.examCode}</p>
                        </div>
                      )}
                    </div>

                    {request.notes && (
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm font-semibold text-gray-800">Admin Notes</p>
                        <p className="text-sm text-gray-600">{request.notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="ml-4 flex gap-2">
                    {request.status === "pending" && (
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedRequest(request);
                          setStatus("approved");
                          setExamCode("");
                          setNotes("");
                        }}
                      >
                        Review
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardContent className="pt-6">
              <h2 className="text-xl font-bold mb-2">Review Exam Request</h2>
              <p className="text-sm text-gray-600 mb-4">
                {selectedRequest.firstName} {selectedRequest.lastName} - {selectedRequest.course.title}
              </p>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full mt-1 p-2 border rounded-lg"
                  >
                    <option value="approved">Approve</option>
                    <option value="rejected">Reject</option>
                  </select>
                </div>
                {status === "approved" && (
                  <div>
                    <Label htmlFor="examCode">Exam Code {selectedRequest.examType === "computer-based" ? "*" : "(Optional)"}</Label>
                    <Input
                      id="examCode"
                      value={examCode}
                      onChange={(e) => setExamCode(e.target.value)}
                      placeholder="e.g., EXAM-2024-001"
                      className="mt-1"
                    />
                    {selectedRequest.examType === "paper-based" && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Note:</strong> For paper-based exams, the student will receive an email from PeopleCert to complete their registration once the booking is confirmed.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label htmlFor="notes">Admin Notes</Label>
                  <textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes..."
                    className="w-full mt-1 p-2 border rounded-lg"
                    rows={3}
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedRequest(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpdateStatus}
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? "Updating..." : "Update Status"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
